<template>
  <div class="admin">
    <h1>Facilitator hub</h1>
    <p class="muted">
      Manage college+date sessions, approve each student against a workshop date, then open the
      deck. Admins are not assigned to a college — pick a session below to view or clear that
      room's votes.
    </p>
    <div class="actions">
      <router-link class="btn btn-primary" to="/admin/colleges">Manage colleges</router-link>
      <router-link class="btn btn-secondary" to="/admin/users">Manage users</router-link>
      <router-link class="btn btn-secondary" to="/select-college">View a session</router-link>
      <router-link class="btn btn-secondary" to="/slides/01">Open slide deck</router-link>
      <router-link class="btn btn-secondary" to="/admin/guides">Guides</router-link>
    </div>

    <section class="card polls">
      <h2>Polls</h2>
      <p class="muted">
        Clear vote numbers for the college and date you are viewing. This does not assign you to
        that college. You can also clear one slide at a time from Results on the deck.
      </p>
      <div class="scope">
        <label>
          College
          <select v-model="collegeName" :disabled="busy">
            <option disabled value="">Select college</option>
            <option v-for="n in collegeNames" :key="n" :value="n">{{ n }}</option>
          </select>
        </label>
        <label v-if="!everydaySelected">
          Date
          <select v-model="workshopDate" :disabled="busy || !dates.length">
            <option disabled value="">Select date</option>
            <option v-for="d in dates" :key="d" :value="d">{{ formatDate(d) }}</option>
          </select>
        </label>
        <p v-else class="muted everyday">Admin Test University is live every day.</p>
      </div>
      <p v-if="error" class="error">{{ error }}</p>
      <p v-else-if="status" class="status">{{ status }}</p>
      <button
        type="button"
        class="btn btn-secondary"
        :disabled="busy || !canClear"
        @click="clearAll"
      >
        {{ busy ? 'Clearing…' : 'Clear all votes for this session' }}
      </button>
    </section>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { slides } from '../content/deck'
import { isAdminTestUniversity, isEverydaySession } from '../services/adminTestUniversity'
import { getAdminSession, setAdminSession } from '../services/adminSession'
import { adminListColleges, clearPollResults } from '../services/api'

const sessions = ref([])
const collegeName = ref('')
const workshopDate = ref('')
const busy = ref(false)
const error = ref('')
const status = ref('')
const ready = ref(false)

const collegeNames = computed(() => {
  const seen = new Map()
  for (const s of sessions.value) {
    if (!s.name) continue
    if (!s.workshopDate && !isEverydaySession(s)) continue
    const key = s.name.toLowerCase()
    if (!seen.has(key)) seen.set(key, s.name)
  }
  return [...seen.values()].sort((a, b) => a.localeCompare(b))
})

const everydaySelected = computed(() => isAdminTestUniversity(collegeName.value))
const canClear = computed(
  () => !!collegeName.value && (everydaySelected.value || !!workshopDate.value),
)

const dates = computed(() => {
  if (!collegeName.value) return []
  const key = collegeName.value.toLowerCase()
  return sessions.value
    .filter((s) => (s.name || '').toLowerCase() === key && s.workshopDate)
    .map((s) => s.workshopDate)
    .sort()
})

function formatDate(iso) {
  if (!iso) return ''
  try {
    return new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  } catch {
    return iso
  }
}

watch([collegeName, workshopDate], ([name, date], [prevName]) => {
  if (!ready.value) return
  status.value = ''
  if (name !== prevName && date && !dates.value.includes(date)) {
    workshopDate.value = dates.value.length === 1 ? dates.value[0] : ''
    return
  }
  setAdminSession({ collegeName: name, workshopDate: date })
})

async function clearAll() {
  if (!canClear.value || busy.value) return
  const when = everydaySelected.value ? 'every day' : formatDate(workshopDate.value)
  const ok = confirm(
    `Clear every poll for ${collegeName.value}${when ? ` (${when})` : ''}? Students can vote again.`,
  )
  if (!ok) return
  busy.value = true
  error.value = ''
  status.value = ''
  try {
    setAdminSession({
      collegeName: collegeName.value,
      workshopDate: workshopDate.value,
    })
    const pollSlides = slides.filter((s) => s.poll)
    let deleted = 0
    for (const s of pollSlides) {
      const res = await clearPollResults(s.id, {
        collegeName: collegeName.value,
        workshopDate: workshopDate.value,
      })
      deleted += res.deleted || 0
    }
    try {
      window.dispatchEvent(new CustomEvent('u80-poll-cleared'))
    } catch {
      /* ignore */
    }
    status.value =
      deleted > 0
        ? `Cleared ${deleted} vote record${deleted === 1 ? '' : 's'} for ${collegeName.value} on ${when}.`
        : `No votes to clear for ${collegeName.value} on ${when}.`
  } catch (e) {
    error.value = e.message || 'Could not clear votes'
  } finally {
    busy.value = false
  }
}

onMounted(async () => {
  try {
    const list = await adminListColleges()
    sessions.value = list.colleges || []
    const viewing = getAdminSession()
    if (viewing.collegeName) collegeName.value = viewing.collegeName
    if (viewing.workshopDate) workshopDate.value = viewing.workshopDate
  } catch (e) {
    error.value = e.message || 'Could not load sessions'
  } finally {
    ready.value = true
  }
})
</script>

<style scoped>
.admin h1 {
  margin-top: 0;
}

.actions {
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;
  flex-wrap: wrap;
}

.actions a {
  text-decoration: none;
}

.polls {
  margin-top: 2rem;
}

.polls h2 {
  margin: 0 0 0.4rem;
  font-size: 1.15rem;
}

.scope {
  display: grid;
  gap: 0.75rem;
  margin: 1rem 0;
}

@media (min-width: 640px) {
  .scope {
    grid-template-columns: 1fr 1fr;
  }
}

.scope label {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  font-size: 0.8rem;
  font-weight: 600;
}

.scope select {
  font: inherit;
  font-weight: 400;
  padding: 0.55rem 0.65rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--cream);
}

.error {
  color: var(--danger);
  margin: 0 0 0.75rem;
}

.status {
  margin: 0 0 0.75rem;
  font-weight: 600;
}

.everyday {
  align-self: end;
  margin: 0;
}
</style>
